import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { ArrowLeft, ArrowRight, Loader2, Star } from "lucide-react";

const usd = (c) => (c == null ? null : `$${(c / 100).toFixed(2)}`);
const COLLECTION_ORDER = ["Foundations", "Continuity", "Clinical Support", "Immersion"];
const FEATURED_SLUG = "therapeutic-immersion";
const motif = (slug) => `/offerings/${slug}.jpg`;

const priceLine = (p) => {
  if (p.is_application) return "By application";
  if (p.is_recurring) {
    const hasIntro = p.intro_price_cents && p.intro_price_cents !== (p.standard_price_cents || p.price_cents);
    if (hasIntro) return `${usd(p.intro_price_cents)} first month · then ${usd(p.standard_price_cents || p.price_cents)}/mo`;
    return `${usd(p.standard_price_cents || p.price_cents)}/month${p.duration_months ? ` · ${p.duration_months} months` : ""}`;
  }
  return `${usd(p.price_cents)} · one-time`;
};

export const OfferingsStrip = ({ className = "" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  const scrollStrip = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 440), behavior: "smooth" });
  };

  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => setProducts(data.products || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className={`flex justify-center py-10 ${className}`}><Loader2 className="h-6 w-6 animate-spin text-ink-muted" /></div>;
  }

  const ordered = COLLECTION_ORDER.flatMap((c) => products.filter((p) => p.collection_name === c));
  const rest = products.filter((p) => !COLLECTION_ORDER.includes(p.collection_name));
  // Natural order ends on the Immersion (its card keeps the "featured" styling, just placed last).
  const strip = [...ordered, ...rest];
  if (!strip.length) return null;

  return (
    <div className={`relative ${className}`} data-testid="offerings-landscape">
      <button type="button" onClick={() => scrollStrip(-1)} aria-label="Previous offerings" data-testid="offerings-scroll-left"
        className="absolute -left-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory-50 text-ink shadow-md ring-1 ring-ivory-400/60 transition hover:text-terracotta hover:ring-terracotta/40 sm:flex">
        <ArrowLeft className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => scrollStrip(1)} aria-label="Next offerings" data-testid="offerings-scroll-right"
        className="absolute -right-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory-50 text-ink shadow-md ring-1 ring-ivory-400/60 transition hover:text-terracotta hover:ring-terracotta/40 sm:flex">
        <ArrowRight className="h-4 w-4" />
      </button>
      <div ref={scrollerRef} className="flex snap-x snap-mandatory items-stretch gap-5 overflow-x-auto pb-5 [-ms-overflow-style:none] [scrollbar-width:thin]">
        {strip.map((p) => {
          const isFeatured = p.slug === FEATURED_SLUG;
          return (
            <Link
              key={p.slug}
              to={`/offerings/${p.slug}`}
              data-testid={`offering-card-${p.slug}`}
              className={`group flex flex-none snap-start flex-col overflow-hidden rounded-2xl bg-ivory-50 text-left shadow-sm ring-1 transition duration-300 hover:-translate-y-1.5 hover:shadow-xl ${isFeatured ? "w-[86vw] max-w-[480px] ring-gold/50 hover:ring-gold/70 sm:w-[460px]" : "w-[80vw] max-w-[380px] ring-ivory-400/60 hover:ring-terracotta/40 sm:w-[340px]"}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={motif(p.slug)}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-[650ms] ease-out group-hover:scale-[1.08] ${isFeatured ? "h-48 sm:h-56" : "h-28 sm:h-32"}`}
                />
                {isFeatured && (
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-ink/85 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-ivory-50 backdrop-blur-sm" data-testid="offering-featured-badge">
                    <Star className="h-3 w-3 text-gold-foil" /> The deep work
                  </span>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-ivory-50 to-transparent" />
              </div>
              <div className="flex flex-1 flex-col px-6 pb-6 pt-3">
                <p className="kicker text-terracotta">{p.collection_name}</p>
                <div className="mt-2 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Door {p.door_number} · {p.tagline}</p>
                    <h2 className={`mt-1 font-display text-ink ${isFeatured ? "text-3xl" : "text-2xl"}`}>{p.title}</h2>
                  </div>
                  <ArrowRight className="mt-1 h-5 w-5 flex-none text-ink-muted transition group-hover:translate-x-1 group-hover:text-terracotta" />
                </div>
                <p className={`mt-3 flex-1 text-sm leading-relaxed text-ink-soft ${isFeatured ? "line-clamp-4" : "line-clamp-5"}`}>{p.description}</p>
                <p className={`mt-4 font-display text-ink ${isFeatured ? "text-xl" : "text-lg"}`}>{priceLine(p)}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-ink-muted sm:hidden">Swipe to explore all offerings →</p>
    </div>
  );
};
